<template>
  <div class="customer-account-query">
    <div class="page-header">
      <h2>会员查询</h2>
      <p>查看和维护会员档案、手机号、头像、余额和所属门店</p>
    </div>

    <div class="page-content">
      <div class="search-section">
        <div class="search-box">
          <select v-model="searchStoreId" class="store-select" @change="handleSearch">
            <option value="">全部门店</option>
            <option v-for="store in storeList" :key="store.id" :value="store.id">{{ store.storeName }}</option>
          </select>
          <input v-model="searchKeyword" class="search-input" placeholder="搜索会员姓名/手机号" @keyup.enter="handleSearch" />
          <button class="search-btn" @click="handleSearch">搜索</button>
          <button class="reset-btn" @click="handleReset">重置</button>
        </div>
      </div>

      <div class="notice-card">
        当前列表来自会员档案表；这里可修改会员姓名、手机号和头像地址，删除会硬删除会员及其关联记录。
      </div>

      <div class="table-section">
        <div class="table-header">
          <h3>会员列表</h3>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>头像</th>
                <th>会员姓名</th>
                <th>手机号</th>
                <th>所属门店</th>
                <th>余额</th>
                <th>累计消费</th>
                <th>会员等级</th>
                <th>状态</th>
                <th>注册时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="10" class="empty-cell">加载中...</td>
              </tr>
              <tr v-else-if="memberList.length === 0">
                <td colspan="10" class="empty-cell">暂无会员</td>
              </tr>
              <tr v-else v-for="member in memberList" :key="member.memberId">
                <td>
                  <img v-if="member.avatarUrl" :src="normalizeAvatarUrl(member.avatarUrl)" class="avatar" />
                  <span v-else class="avatar-placeholder">-</span>
                </td>
                <td>{{ member.memberName || member.nickName || '-' }}</td>
                <td>{{ member.phone || '-' }}</td>
                <td>{{ member.storeName || getStoreName(member.storeId) }}</td>
                <td>{{ formatMoney(member.balance) }}元</td>
                <td>{{ formatMoney(member.totalConsumption) }}元</td>
                <td>{{ member.memberLevelName || '-' }}</td>
                <td>
                  <span :class="['status-badge', member.status === 0 ? 'active' : 'inactive']">
                    {{ member.statusText || (member.status === 0 ? '正常' : '冻结') }}
                  </span>
                </td>
                <td>{{ formatDate(member.registerTime) }}</td>
                <td>
                  <div class="action-buttons">
                    <button class="edit-btn" @click="openEditModal(member)">编辑</button>
                    <button class="delete-btn" @click="deleteCustomerMember(member)">删除</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="edit-modal">
        <div class="modal-header">
          <h3>编辑会员资料</h3>
          <button class="close-btn" @click="closeEditModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>会员姓名</label>
            <input v-model.trim="editForm.nickName" class="form-input" placeholder="请输入会员姓名" />
          </div>
          <div class="form-group">
            <label>手机号</label>
            <input v-model.trim="editForm.phone" class="form-input" placeholder="请输入手机号" maxlength="11" />
          </div>
          <div class="form-group">
            <label>头像地址</label>
            <input v-model.trim="editForm.avatarUrl" class="form-input" placeholder="可粘贴头像 URL 或后端文件路径" />
          </div>
          <div class="avatar-preview-row" v-if="editForm.avatarUrl">
            <span>头像预览</span>
            <img :src="normalizeAvatarUrl(editForm.avatarUrl)" class="avatar preview" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeEditModal">取消</button>
          <button class="save-btn" :disabled="saving" @click="saveCustomerAccount">{{ saving ? '保存中...' : '保存' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'CustomerAccountQuery',
  data() {
    return {
      memberList: [],
      storeList: [],
      searchStoreId: '',
      searchKeyword: '',
      loading: false,
      showEditModal: false,
      saving: false,
      editForm: {
        id: null,
        nickName: '',
        phone: '',
        avatarUrl: '',
        storeId: null,
        remark: ''
      }
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadCustomerList()
  },
  methods: {
    async loadStoreList() {
      const response = await request.get('/api/stores/getStores', { params: { page: 0, size: 1000 } })
      if (response.data.success && response.data.code === 200) {
        this.storeList = response.data.data.content || []
      }
    },
    async loadCustomerList() {
      this.loading = true
      try {
        const response = await request.get('/api/miniapp/admin/members', {
          params: {
            page: 0,
            size: 5000,
            storeId: this.searchStoreId || undefined,
            keyword: this.searchKeyword || undefined
          }
        })
        if (response.data.success && response.data.code === 200) {
          this.memberList = (response.data.data.content || []).map(item => ({
            ...item,
            avatarUrl: item.avatarUrl || ''
          }))
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.loadCustomerList()
    },
    handleReset() {
      this.searchStoreId = ''
      this.searchKeyword = ''
      this.loadCustomerList()
    },
    getStoreName(storeId) {
      const store = this.storeList.find(item => String(item.id) === String(storeId))
      return store ? store.storeName : '-'
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('zh-CN')
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    normalizeAvatarUrl(url) {
      if (!url) return ''
      const value = String(url).trim().replace(/\\/g, '/')
      if (!value) return ''
      if (/^data:/i.test(value)) return value
      if (/^(https?:)?\/\//i.test(value)) return value.replace(/(\/api\/files)\/+/i, '$1/')
      if (value.startsWith('/api/')) return value.replace(/(\/api\/files)\/+/i, '$1/')
      if (value.startsWith('/files/')) return `/api${value}`.replace(/(\/api\/files)\/+/i, '$1/')
      const uploadRoot = '/home/games/'
      const uploadRootIndex = value.indexOf(uploadRoot)
      if (uploadRootIndex >= 0) {
        return `/api/files/${value.substring(uploadRootIndex + uploadRoot.length).replace(/^\/+/, '')}`
      }
      if (value.includes('/avatars/')) {
        return `/api/files/avatars/${value.split('/avatars/')[1].replace(/^\/+/, '')}`
      }
      if (value.startsWith('avatars/')) {
        return `/api/files/${value}`
      }
      return `/api/files/${value.replace(/^\/?files\//, '').replace(/^\/?/, '')}`
    },
    getDisplayName(member) {
      return member.memberName || member.nickName || ''
    },
    openEditModal(member) {
      this.editForm = {
        id: member.memberId,
        nickName: this.getDisplayName(member),
        phone: member.phone || '',
        avatarUrl: member.avatarUrl || '',
        storeId: member.storeId || null,
        remark: member.remark || ''
      }
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
      this.saving = false
    },
    validateEditForm() {
      if (!this.editForm.nickName) {
        alert('请输入昵称或姓名')
        return false
      }
      if (!this.editForm.phone) {
        alert('请输入手机号')
        return false
      }
      if (!/^1[3-9]\d{9}$/.test(this.editForm.phone)) {
        alert('手机号格式不正确')
        return false
      }
      return true
    },
    async saveCustomerAccount() {
      if (!this.validateEditForm()) return
      this.saving = true
      try {
        const response = await request.put(`/api/miniapp/admin/members/${this.editForm.id}`, {
          nickName: this.editForm.nickName,
          phone: this.editForm.phone,
          avatarUrl: this.editForm.avatarUrl || null,
          remark: this.editForm.remark || ''
        })
        if (response.data.success && response.data.code === 200) {
          this.closeEditModal()
          this.loadCustomerList()
        } else {
          throw new Error(response.data.message || '保存失败')
        }
      } catch (error) {
        alert(error.message || '保存失败，请重试')
      } finally {
        this.saving = false
      }
    },
    async deleteCustomerMember(member) {
      const memberId = member.memberId
      if (!memberId) {
        alert('未找到关联会员档案，无法执行会员删除')
        return
      }
      try {
        const name = this.getDisplayName(member) || member.phone || memberId
        const confirmed = window.confirm(`确认删除会员「${name}」？删除后会员及关联充值、消费、上机、消息等记录会被硬删除，无法恢复。`)
        if (!confirmed) return
        const response = await request.delete(`/api/miniapp/admin/members/${memberId}`)
        if (response.data.success && response.data.code === 200) {
          alert('删除成功')
          this.loadCustomerList()
        } else {
          throw new Error(response.data.message || '删除失败')
        }
      } catch (error) {
        alert(error.message || '删除失败，请重试')
      }
    }
  }
}
</script>

<style scoped>
.page-header { margin-bottom: 24px; }
.page-header h2 { margin: 0 0 8px; color: #333; }
.page-header p { margin: 0; color: #666; }
.page-content, .table-section { background: #fff; border-radius: 8px; padding: 20px; }
.search-section { margin-bottom: 16px; }
.search-box { display: flex; flex-wrap: wrap; gap: 12px; }
.search-input, .store-select { height: 36px; padding: 0 12px; border: 1px solid #d9d9d9; border-radius: 6px; }
.search-input { width: 260px; }
button { border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; }
.search-btn { background: #1890ff; color: #fff; }
.reset-btn { background: #f5f5f5; color: #333; }
.action-buttons { display: flex; gap: 8px; }
.edit-btn { background: #e6f7ff; color: #0958d9; padding: 6px 12px; }
.delete-btn { background: #fff1f0; color: #cf1322; padding: 6px 12px; }
.notice-card { padding: 12px 16px; margin-bottom: 20px; border: 1px solid #91d5ff; background: #e6f7ff; border-radius: 6px; color: #0958d9; }
.table-container { overflow-x: auto; }
.data-table { width: 100%; min-width: 1100px; border-collapse: collapse; }
.data-table th, .data-table td { padding: 12px; border-bottom: 1px solid #f0f0f0; text-align: left; }
.empty-cell { text-align: center; color: #999; padding: 40px; }
.avatar { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; }
.avatar-placeholder { display: inline-flex; width: 36px; height: 36px; align-items: center; justify-content: center; background: #f5f5f5; border-radius: 50%; color: #999; }
.mono-cell { max-width: 180px; font-family: Consolas, monospace; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.status-badge { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.status-badge.active { background: #f6ffed; color: #52c41a; }
.status-badge.inactive { background: #fff7e6; color: #fa8c16; }
.modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(0, 0, 0, 0.45); display: flex; align-items: center; justify-content: center; }
.edit-modal { width: 520px; background: #fff; border-radius: 10px; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2); overflow: hidden; }
.modal-header { padding: 18px 22px; border-bottom: 1px solid #f0f0f0; display: flex; justify-content: space-between; align-items: center; }
.modal-header h3 { margin: 0; color: #333; }
.close-btn { padding: 0; width: 28px; height: 28px; background: transparent; font-size: 22px; color: #999; }
.modal-body { padding: 22px; }
.form-group { margin-bottom: 16px; }
.form-group label { display: block; margin-bottom: 8px; color: #333; font-weight: 600; }
.form-input { width: 100%; height: 38px; box-sizing: border-box; border: 1px solid #d9d9d9; border-radius: 6px; padding: 0 12px; }
.avatar-preview-row { display: flex; align-items: center; gap: 12px; color: #666; }
.avatar.preview { width: 48px; height: 48px; }
.modal-footer { padding: 16px 22px; border-top: 1px solid #f0f0f0; display: flex; justify-content: flex-end; gap: 12px; }
.cancel-btn { background: #f5f5f5; color: #333; }
.save-btn { background: #1890ff; color: #fff; }
.save-btn:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
